import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { NewsMode, Segment, TopicTensionRecord } from '../../types';
import { SEGMENT_COLORS, SEGMENT_LABELS } from '../../constants';
import { tooltipStyles } from '../../utils/chartStyles';
import NewsModeToggle from '../common/NewsModeToggle';

interface TopicTensionChartProps {
  data: Record<NewsMode, TopicTensionRecord[]>;
  newsMode: NewsMode;
  onNewsModeChange: (mode: NewsMode) => void;
}

interface ChartRecord {
  topic: string;
  leftPercentage: number;
  rightPercentage: number;
  leftValue: number;
  rightValue: number;
  fullTopic: string;
  group: 'Left' | 'Right';
}

const wrapLabel = (text: string, maxChars = 35) => {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines;
};

const TopicTensionChart = ({ data, newsMode, onNewsModeChange }: TopicTensionChartProps) => {
  const chartData = useMemo<ChartRecord[]>(() => {
    const records = data[newsMode] ?? [];
    const sorted = [...records].sort(
      (a, b) => Math.abs(b.left - b.right) - Math.abs(a.left - a.right)
    );
    const picked = sorted.slice(0, 40);
    const grouped: ChartRecord[] = picked.map((item) => {
      const total = item.left + item.right || 1;
      const leftPercentage = (item.left / total) * 100;
      const rightPercentage = (item.right / total) * 100;
      const group: 'Left' | 'Right' = rightPercentage >= leftPercentage ? 'Right' : 'Left';
      return {
        topic: item.topic,
        fullTopic: item.topic,
        leftPercentage: group === 'Left' ? -leftPercentage : 0,
        rightPercentage: group === 'Right' ? rightPercentage : 0,
        leftValue: leftPercentage,
        rightValue: rightPercentage,
        group
      };
    });
    const right = grouped.filter((item) => item.group === 'Right').slice(0, 8);
    const left = grouped.filter((item) => item.group === 'Left').slice(0, 8);
    right.sort((a, b) => Math.abs(b.rightPercentage - b.leftPercentage) - Math.abs(a.rightPercentage - a.leftPercentage));
    left.sort((a, b) => Math.abs(b.leftPercentage - b.rightPercentage) - Math.abs(a.leftPercentage - a.rightPercentage));
    return [...right, ...left];
  }, [data, newsMode]);

  return (
    <>
      <div className="chart-controls stacked">
        <NewsModeToggle value={newsMode} onChange={onNewsModeChange} />
      </div>
      <div className="chart-wrapper stacked">
        <ResponsiveContainer width="100%" height={900}>
          <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 20, left: 20, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis
            type="number"
            domain={[-100, 100]}
            tickFormatter={(value) => `${Math.abs(value)}%`}
            tick={{ fill: '#cfd4ee' }}
          />
          <YAxis
            type="category"
            dataKey="topic"
            width={240}
            tick={(props) => {
              const { x = 0, y = 0, payload } = props;
              const lines = wrapLabel(String(payload.value));
              return (
                <text x={x - 6} y={y} textAnchor="end" fill="#dfe6ff" fontSize={13}>
                  {lines.map((line, idx) => (
                    <tspan key={idx} x={x - 6} dy={idx === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.08)' }}
            formatter={(value: number, _name, entry) => {
              const record = entry?.payload as ChartRecord;
              const actual =
                entry?.dataKey === 'leftPercentage'
                  ? record.leftValue
                  : record.rightValue;
              return [`${Math.abs(actual).toFixed(1)}%`, entry?.dataKey === 'leftPercentage' ? SEGMENT_LABELS.Left : SEGMENT_LABELS.Right];
            }}
            labelFormatter={(_, payload) => (payload && payload[0] ? (payload[0].payload as ChartRecord).fullTopic : '')}
            {...tooltipStyles}
          />
          <ReferenceLine x={0} stroke="#44495f" />
          <Bar dataKey="leftPercentage" fill={SEGMENT_COLORS.Left} radius={[12, 12, 12, 12]} />
          <Bar dataKey="rightPercentage" fill={SEGMENT_COLORS.Right} radius={[12, 12, 12, 12]} />
        </BarChart>
      </ResponsiveContainer>
        <div className="chart-legend">
          <span className="badge" style={{ background: SEGMENT_COLORS.Right }}>Right-dominant</span>
          <span className="badge" style={{ background: SEGMENT_COLORS.Left }}>Left-dominant</span>
        </div>
      </div>
    </>
  );
};

export default TopicTensionChart;
